import { createFileRoute, Link, redirect, useParams } from '@tanstack/react-router'
import { Card } from '@journ/bloom/card'
import { StaggerItem } from '@thom/folio-ui/components/motion/stagger'
import { useClient } from '@thom/folio-ui/app/use-client'
import { RecordGone } from '@thom/folio-ui/components/record/record-gone'
import { useDomains } from '@thom/folio-ui/app/use-domains'
import { Status } from '@thom/folio-domain/lib/async-status'
import { LoadError } from '@thom/folio-ui/components/load-error'

const ClientPage = () => {
	const { client: slug } = useParams({ from: '/_authenticated/$client/' })
	const client = useClient(slug)
	const state = useDomains(
		client.status === Status.Ready ? { clientId: client.client.id } : undefined,
		client.status !== Status.Ready,
	)

	if (client.status === Status.Failed) return <LoadError message={client.message} />
	if (client.status === Status.Gone) {
		return (
			<RecordGone title={client.title}>
				<Link to='/' className='text-sm underline'>
					Back to clients
				</Link>
			</RecordGone>
		)
	}

	return (
		<div className='space-y-6'>
			<div className='space-y-1'>
				<h1 className='font-serif text-3xl'>{client.status === Status.Ready ? client.client.name : slug}</h1>
				{client.status === Status.Ready && client.client.descr && (
					<p className='text-dim text-sm'>{client.client.descr}</p>
				)}
			</div>

			{state.status === Status.Failed && <LoadError message={state.message} />}

			{state.status === Status.Ready && state.domains.length === 0 && (
				<p className='text-dim text-sm'>No domains in this client yet.</p>
			)}

			{state.status === Status.Ready && state.domains.length > 0 && (
				<div className='grid gap-4 sm:grid-cols-2'>
					{state.domains.map((domain, index) => (
						<StaggerItem key={domain.id} index={index}>
							<Link to='/$client/$domain' params={{ client: slug, domain: domain.slug }} className='block'>
								<Card.Root className='hover:bg-accent/40 gap-1.5 transition-colors'>
									<Card.Title className='mb-2 leading-none'>{domain.name}</Card.Title>

									<Card.Description>{domain.descr || 'No description.'}</Card.Description>

									<div className='text-dimmer flex items-center gap-2 pt-2 text-xs'>
										<span>{domain.slug}</span>
										<span>&middot;</span>
										<span>
											{domain.members.length} {domain.members.length === 1 ? 'member' : 'members'}
										</span>
									</div>
								</Card.Root>
							</Link>
						</StaggerItem>
					))}
				</div>
			)}
		</div>
	)
}

export const Route = createFileRoute('/_authenticated/$client/')({
	beforeLoad: async ({ context, params }) => {
		const client = await context.container.clients.get(params.client)
		if (!client.success) return

		const domains = await context.container.domains.list({ clientId: client.value.id })
		const [domain] = domains.success && domains.value.length === 1 ? domains.value : []
		if (!domain) return

		const projects = await context.container.projects.list({ domainId: domain.id })
		const [project] = projects.success && projects.value.length === 1 ? projects.value : []
		if (project) {
			throw redirect({
				to: '/$client/$domain/$slug',
				params: { client: params.client, domain: domain.slug, slug: project.slug },
			})
		}

		throw redirect({ to: '/$client/$domain', params: { client: params.client, domain: domain.slug } })
	},
	component: ClientPage,
})
